import AssessmentIcon from '@mui/icons-material/Assessment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CampaignIcon from '@mui/icons-material/Campaign';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import ContactsIcon from '@mui/icons-material/Contacts';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import FacebookIcon from '@mui/icons-material/Facebook';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import HttpIcon from '@mui/icons-material/Http';
import InsightsIcon from '@mui/icons-material/Insights';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import PaymentsIcon from '@mui/icons-material/Payments';
import PeopleIcon from '@mui/icons-material/People';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SecurityIcon from '@mui/icons-material/Security';
import SellIcon from '@mui/icons-material/Sell';
import ShieldIcon from '@mui/icons-material/Shield';
import StorageIcon from '@mui/icons-material/Storage';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import { alpha } from '@mui/material/styles';
import { CalendarIcon, CarIcon, MenuIcon, StoreIcon } from 'assets/icons';
import { useBusinessUnits } from 'contexts/BusinessUnitsContext';
import { useSidebar } from 'contexts/SidebarContext';
import useCurrentRoute from 'hooks/useCurrentRoute';
import useCurrentUser from 'hooks/useCurrentUser';
import { useState } from 'react';
import {
  MOBILE_FILTER_BAR_TOP,
  MOBILE_HAMBURGER_HEIGHT,
  MOBILE_HAMBURGER_LEFT,
  MOBILE_HAMBURGER_WIDTH,
} from 'styles/tokens';
import { AUTOSRELIF_BUSINESS_UNIT_ID, INSUREX_BUSINESS_UNIT_ID } from 'utils/constants';

import SidebarFooter from './SidebarFooter';
import SidebarHeader from './SidebarHeader';
import SidebarNav from './SidebarNav';
import { NAVY, NAVY_SHADOW, SURFACE_CONTAINER_LOWEST } from './sidebar.styles';
import type { NavItem, NavSection } from './sidebar.types';

// Non-admin user IDs allowed to see the admin Campaigns page. Jordan and Felipe
// Mirror the backend list in apps/saas_back/src/middlewares/campaignsAccess.ts.
const CAMPAIGNS_ALLOWED_USER_IDS: number[] = [89, 580];

const SideBar = () => {
  const { ignoreMobileSidebarMenuButton } = useCurrentRoute();
  const currentUser = useCurrentUser();
  const { businessUnits } = useBusinessUnits();
  const { collapsed, toggleCollapsed, drawerWidth, selectedBusinessUnitId } = useSidebar();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chatWarningOpen, setChatWarningOpen] = useState(false);

  const [sidebarHovered, setSidebarHovered] = useState(false);

  // -- Role flags (cumulative hierarchy) --
  const isAdmin = !!currentUser?.isAdmin;
  const isBusinessUnitAdmin = isAdmin || currentUser?.role === 'businessUnitAdmin';
  const isBranchManager = isBusinessUnitAdmin || currentUser?.role === 'branchManager';
  const isSeller = currentUser?.role === 'seller' || currentUser?.role === 'sellerWithAssignment';

  // -- Business unit flags --
  const selectedBusinessUnit = businessUnits?.find((bu) => bu.id === selectedBusinessUnitId);
  const isRentACarOrContactCenter =
    selectedBusinessUnit?.name === 'Rent a Car Interno' || selectedBusinessUnit?.name === 'Contact Center';
  const showInsuranceNavItems = selectedBusinessUnit?.type === 'insurance_broker' && selectedBusinessUnit?.id !== 39;
  const showBrokerDealershipItems =
    selectedBusinessUnit?.type === 'car_dealership' && !!selectedBusinessUnit?.brokerBusinessUnitId;
  const showSellerDashboard = !!selectedBusinessUnit?.hasSellerManagement;
  // AutosRelif is a car_dealership demo BU that also showcases the agenda card.
  const showServiceCalendar =
    selectedBusinessUnit?.type === 'after_sales' || selectedBusinessUnit?.id === AUTOSRELIF_BUSINESS_UNIT_ID;
  const showBranchOffices =
    selectedBusinessUnit?.type === 'car_dealership' || selectedBusinessUnit?.type === 'after_sales';

  const handleChatClick = () => {
    if (!selectedBusinessUnit?.pusherIdentification) {
      setChatWarningOpen(true);
      return;
    }
    window.open(`/demochat/${selectedBusinessUnit.pusherIdentification}`, '_blank');
  };

  // -- Build navigation sections --

  const clientsItems: NavItem[] = [{ text: 'Clientes', icon: <ContactsIcon />, path: '/clients' }];

  if (showServiceCalendar) {
    clientsItems.push({ text: 'Agenda', icon: <CalendarIcon />, path: '/calendar' });
  }

  // Sales mirrored from Salesforce only exist for Mueve Seguro. No role gate: the endpoint scopes
  // executives down to the sales of their own clients.
  if (selectedBusinessUnit?.id === INSUREX_BUSINESS_UNIT_ID) {
    clientsItems.push({ text: 'Ventas', icon: <SellIcon />, path: '/sales' });
  }

  if (showSellerDashboard && isBranchManager) {
    clientsItems.push({ text: 'Mis Ejecutivos', icon: <SupervisorAccountIcon />, path: '/mis-ejecutivos' });
  }

  if (!isSeller) {
    clientsItems.push({
      text: 'Encuestas',
      icon: <AssignmentIcon />,
      path: '/surveys',
    });
  }

  if (isRentACarOrContactCenter) {
    clientsItems.push({ text: 'Chat', icon: <WhatsAppIcon />, path: '/demochat', onClick: handleChatClick });
  }

  if (showInsuranceNavItems) {
    clientsItems.push(
      { text: 'Seguros', icon: <ShieldIcon />, path: '/broker-dashboard' },
      { text: 'Cotizar Seguro', icon: <RequestQuoteIcon />, path: '/insurance-quote-form' }
    );
  }

  if (showBrokerDealershipItems) {
    clientsItems.push(
      { text: 'Seguros', icon: <ShieldIcon />, path: '/broker-dashboard' },
      { text: 'Cotizar', icon: <RequestQuoteIcon />, path: '/bci-seguros-quote' }
    );
  }

  const clientsSection: NavSection = { label: 'Clientes', items: clientsItems };

  const reportingItems: NavItem[] = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Leads', icon: <FilterAltIcon />, path: '/leads' },
    { text: 'Reportes', icon: <AssessmentIcon />, path: '/reports' },
    { text: 'Resultado Campañas', icon: <InsightsIcon />, path: '/campaigns-stats' },
    { text: 'Resultados Encuestas', icon: <AssignmentIcon />, path: '/surveys-results' },
  ];

  if (!isSeller) {
    reportingItems.push({ text: 'Salud WhatsApp', icon: <WhatsAppIcon />, path: '/whatsapp-health' });
  }

  const reportingSection: NavSection = { label: 'Reportería', items: reportingItems };

  const settingsItems: NavItem[] = [];

  if (isBusinessUnitAdmin) {
    settingsItems.push({ text: 'Agente', icon: <SupportAgentIcon />, path: '/information' });
    settingsItems.push({ text: 'Meta Ads', icon: <FacebookIcon />, path: '/meta-config' });

    if (selectedBusinessUnit?.type === 'car_dealership') {
      settingsItems.push({ text: 'Autos', icon: <CarIcon />, path: '/cars' });
    }

    if (showInsuranceNavItems) {
      settingsItems.push({ text: 'Credenciales', icon: <SecurityIcon />, path: '/insurance-credentials' });
    }
  }

  if (isBranchManager) {
    settingsItems.push({ text: 'Usuarios', icon: <PeopleIcon />, path: '/users' });
    if (showBranchOffices) {
      settingsItems.push({ text: 'Sucursales', icon: <StoreIcon />, path: '/branch-offices' });
    }
  }

  const settingsSection: NavSection = { label: 'Ajustes', items: settingsItems };

  const adminSection: NavSection = {
    label: 'Admin',
    items: [
      { text: 'Empresas', icon: <CorporateFareIcon />, path: '/businessUnits' },
      { text: 'Costos', icon: <PaymentsIcon />, path: '/businessUnit-charts' },
      { text: 'Campañas', icon: <CampaignIcon />, path: '/admin-campaigns' },
      { text: 'Dashboard Interno', icon: <DashboardCustomizeIcon />, path: '/relif-dashboard' },
      { text: 'DB Reader', icon: <StorageIcon />, path: '/db-reader' },
      { text: 'Monitoreo Salud', icon: <MonitorHeartIcon />, path: '/integration-status' },
      { text: 'URLs Form', icon: <HttpIcon />, path: '/urls-form' },
    ],
  };

  const supportSection: NavSection = {
    label: 'Soporte',
    items: [{ text: 'Soporte', icon: <ContactSupportOutlinedIcon />, path: '/tickets' }],
  };

  const sections: NavSection[] = [clientsSection];

  if (isBranchManager) {
    sections.push(reportingSection);
    sections.push(settingsSection);
  }

  if (isAdmin) {
    sections.push(adminSection);
  } else if (currentUser?.id != null && CAMPAIGNS_ALLOWED_USER_IDS.includes(currentUser.id)) {
    sections.push({
      label: 'Admin',
      items: [{ text: 'Campañas', icon: <CampaignIcon />, path: '/admin-campaigns' }],
    });
  }

  sections.push(supportSection);

  // -- Drawer content (shared between mobile and desktop) --
  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: SURFACE_CONTAINER_LOWEST }}>
      <SidebarHeader onMobileClose={() => setMobileOpen(false)} />
      <SidebarNav sections={sections} onMobileClose={() => setMobileOpen(false)} />
      <SidebarFooter />
    </Box>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <IconButton
        aria-label="open drawer"
        onClick={() => setMobileOpen(!mobileOpen)}
        size="small"
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          top: `${MOBILE_FILTER_BAR_TOP}px`,
          left: `${MOBILE_HAMBURGER_LEFT}px`,
          zIndex: 100,
          width: MOBILE_HAMBURGER_WIDTH,
          height: MOBILE_HAMBURGER_HEIGHT,
          borderRadius: '12px',
          bgcolor: SURFACE_CONTAINER_LOWEST,
          boxShadow: `0px 2px 8px ${alpha(NAVY, 0.1)}`,
          color: NAVY,
          '&:hover': { bgcolor: alpha(NAVY, 0.06) },
          ...(ignoreMobileSidebarMenuButton && { display: 'none' }),
        }}
      >
        <MenuIcon sx={{ fontSize: '1.1rem' }} />
      </IconButton>

      {/* Desktop collapse/expand toggle — arrow sits on the sidebar border, shown on hover */}
      <IconButton
        aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
        onClick={toggleCollapsed}
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
        size="small"
        sx={{
          display: { xs: 'none', md: 'flex' },
          position: 'fixed',
          top: '92px',
          left: `calc(${drawerWidth}px - 12px)`,
          transition: 'left 0.56s cubic-bezier(0.25, 0.8, 0.25, 1), opacity 0.2s ease',
          zIndex: 1300,
          width: 24,
          height: 24,
          opacity: sidebarHovered ? 1 : 0,
          color: alpha(NAVY, 0.5),
          backgroundColor: '#ffffff',
          border: `1px solid ${alpha(NAVY, 0.1)}`,
          boxShadow: NAVY_SHADOW,
          '&:hover': { backgroundColor: SURFACE_CONTAINER_LOWEST, color: NAVY },
        }}
      >
        {collapsed ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
      </IconButton>

      <Snackbar
        open={chatWarningOpen}
        autoHideDuration={5000}
        onClose={() => setChatWarningOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 16, md: 24 } }}
      >
        <Alert
          severity="warning"
          onClose={() => setChatWarningOpen(false)}
          sx={{
            backgroundColor: NAVY,
            color: '#fff',
            fontFamily: 'Jost, sans-serif',
            fontWeight: 600,
            fontSize: '14px',
            borderRadius: '12px',
            boxShadow: `0px 12px 32px rgba(21, 24, 58, 0.25)`,
            backdropFilter: 'blur(12px)',
            '& .MuiAlert-icon': { color: '#F9D00A' },
            '& .MuiAlert-action .MuiIconButton-root': { color: alpha('#fff', 0.7) },
          }}
        >
          Selecciona una unidad de negocio para abrir el chat.
        </Alert>
      </Snackbar>

      <Box
        component="nav"
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
        sx={{
          width: { xs: '100%', md: drawerWidth },
          flexShrink: 0,
          transition: 'width 0.56s cubic-bezier(0.25, 0.8, 0.25, 1)',
        }}
      >
        {/* Mobile temporary drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: '100%', boxShadow: NAVY_SHADOW, border: 0 },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop permanent drawer */}
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 0,
              boxShadow: NAVY_SHADOW,
              transition: 'width 0.56s cubic-bezier(0.25, 0.8, 0.25, 1)',
              overflowX: 'hidden',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
    </>
  );
};

export default SideBar;
